<template>
  <BasePage :container="false">
    <Loader v-if="loading"/>
    <section v-else>

      <div class="columns is-mobile">

        <div v-if="step===0" class="column is-6 is-offset-3">
          <section id="intro">
            <h1 class="title is-1 exercise--title">{{ exercise != null ? exercise.title : 'huh?'}}</h1>

            <p>Welcome to DIY Flex! This program is designed to help you leverage your classroom video to grow your teaching practice and drive your professional development.</p>

            <p>Working through this process with a partner will allow you to earn <strong>1 Academic Ladder Credit (ALC)</strong>,which can be used for salary lane advancement. To learn more about ALCs, please click here.</p>

            <p>If you have any questions or would like to register, please reach out to <a href="mailto:kelly@classforward.com">kelly@classforward.com</a></p>
          </section>

          <div class="menu-list">

            <div @click="saveAndAdvance(1)">Introductory Reflection
              <CircledCheckMarkIcon v-if="completionStatus.introReflectionComplete" class="completed--icon" />
            </div>

            <div @click="displaySR = !displaySR">Self-Reflection (x3)
              <CircledCheckMarkIcon v-if="completionStatus.selfReflection1Complete" class="completed--icon" />
              <ChevronIcon v-bind:class="{'upside-down':displaySR}"/>
            </div>

            <section v-if="displaySR" class="drop-down">
              <ExerciseDropdown
                :isCoaching="false"
                :saveAndAdvance="saveAndAdvance"
                :saveAndAdvanceIndex="2"
                :title="'Self Reflection 1'"
                :videoComplete="this.exercise.self_reflection1_video"
                :commentComplete="this.exercise.self_reflection1_comments"
                :reflectionComplete="this.selfReflection1"
              />

              <ExerciseDropdown
                :isCoaching="false"
                :saveAndAdvance="saveAndAdvance"
                :saveAndAdvanceIndex="3"
                :title="'Self Reflection 2'"
                :videoComplete="this.exercise.self_reflection2_video"
                :commentComplete="this.exercise.self_reflection2_comments"
                :reflectionComplete="this.selfReflection2"
              />

              <ExerciseDropdown
                :isCoaching="false"
                :saveAndAdvance="saveAndAdvance"
                :saveAndAdvanceIndex="4"
                :title="'Self Reflection 3'"
                :videoComplete="this.exercise.self_reflection3_video"
                :commentComplete="this.exercise.self_reflection3_comments"
                :reflectionComplete="this.selfReflection3"
              />

            </section>

            <div @click="displayCC = !displayCC" >Coaching Conversation (x3)
              <CircledCheckMarkIcon v-if="completionStatus.coachingConversation1Complete" class="completed--icon" />
              <ChevronIcon v-bind:class="{'upside-down':displayCC}"/>
            </div>

            <section v-if="displayCC" class="drop-down">
              <ExerciseDropdown
                :isCoaching="true"
                :saveAndAdvance="saveAndAdvance"
                :saveAndAdvanceIndex="5"
                :title="'Coaching Conversation 1'"
                :videoComplete="this.exercise.coaching_conversation1_video"
                :commentComplete="this.exercise.coaching_conversation1_comments"
                :reflectionComplete="this.coachingConversation1Reflection"
                :coachingComplete="this.coachingConversation1Coaching"
              />

              <ExerciseDropdown
                :isCoaching="true"
                :saveAndAdvance="saveAndAdvance"
                :saveAndAdvanceIndex="6"
                :title="'Coaching Conversation 2'"
                :videoComplete="this.exercise.coaching_conversation2_video"
                :commentComplete="this.exercise.coaching_conversation2_comments"
                :reflectionComplete="this.coachingConversation2Reflection"
                :coachingComplete="this.coachingConversation2Coaching"
              />

              <ExerciseDropdown
                :isCoaching="true"
                :saveAndAdvance="saveAndAdvance"
                :saveAndAdvanceIndex="7"
                :title="'Coaching Conversation 3'"
                :videoComplete="this.exercise.coaching_conversation3_video"
                :commentComplete="this.exercise.coaching_conversation3_comments"
                :reflectionComplete="this.coachingConversation3Reflection"
                :coachingComplete="this.coachingConversation3Coaching"
              />

            </section>

            <div @click="saveAndAdvance(8)">Final Reflection
              <CircledCheckMarkIcon v-if="completionStatus.finalReflectionComplete" class="completed--icon" />
            </div>
          </div>
        </div>

        <div class="column is-half is-offset-one-quarter">
          <template v-if="step === 1">
            <div class="box">

              <section style="display: flex; justify-content: space-between;">
                <h1>INTRODUCTORY REFLECTION</h1>
              </section>

              <p>Given your work last year in DIY, name <strong>your instructional improvement goal</strong> for DIY Flex. Clearly state your goal, provide your rationale for choosing this based on previous analysis of instruction, and attend to these guiding questions.</p>
              <ul>
                <li>How would this create a more equitable learning environment?</li>
                <li>How does this develop a student-centered and collaborative classroom environment?</li>
                <li>How will this advance student understanding of the content?</li>
              </ul>
              <textarea class="textarea" v-model="exercise.intro_reflection_text"></textarea>
            </div>
            <span>
              <button class="button is-info" @click="saveExercise()">Save</button>
              <button class="button is-primary" @click="saveAndAdvance(0)">Home</button>
            </span>
          </template>

          <template v-if="step === 2">
            <div class="box">
              <section style="display: flex; justify-content: space-between;">
                <h1 :data-balloon=this.toolTipSR data-balloon-pos="down" data-balloon-break>SELF-REFLECTION 1</h1>
              </section>

                <span>Video: (select from drop-down)</span>
                <br />
                <div class="select">
                  <select v-model="exercise.self_reflection1_video">
                    <option v-for="option in videos" v-bind:value="option.id">
                      {{ option.title }}
                    </option>
                  </select>
                </div>
                <br />
                <input type="checkbox" v-model="exercise.self_reflection1_comments" /> Check this box to indicate that you have made at least 5 comments on your video.

                <p>What did you notice?</p>
                <textarea class="textarea" v-model="exercise.self_reflection1_text1"></textarea>

                <p>In the video artifact, where do you see examples of your practice moving towards your instructional goal?</p>
                <textarea class="textarea" v-model="exercise.self_reflection1_text2"></textarea>

                <p>Analyze one instructional move you captured and what adjustment you would make to move closer to your stated instructional improvement goal.</p>
                <textarea class="textarea" v-model="exercise.self_reflection1_text3"></textarea>

                <p>How has this reflection impacted your thinking about your teaching moving forward?</p>
                <textarea class="textarea" v-model="exercise.self_reflection1_text4"></textarea>

                <p>What will you focus on for the next round of videotaping and what is at least one specific next step you will take to move closer to your goal?</p>
                <textarea class="textarea" v-model="exercise.self_reflection1_text5"></textarea>
              </div>
              <span>
                <button class="button is-info" @click="saveExercise()">Save</button>
                <button class="button is-primary" @click="saveAndAdvance(0)">Home</button>
              </span>
            </template>

            <template v-if="step === 3">
              <div class="box">
                <section style="display: flex; justify-content: space-between;">
                  <h1 :data-balloon=this.toolTipSR data-balloon-pos="down" data-balloon-break>SELF-REFLECTION 2</h1>
                </section>

                <span>Video: (select from drop-down)</span>
                <br />
                <div class="select">
                  <select v-model="exercise.self_reflection2_video">
                    <option v-for="option in videos" v-bind:value="option.id">
                      {{ option.title }}
                    </option>
                  </select>
                </div>
                <br />
                <input type="checkbox" v-model="exercise.self_reflection2_comments" /> Check this box to indicate that you have made at least 5 comments on your video.

                <p>What did you notice?</p>
                <textarea class="textarea" v-model="exercise.self_reflection2_text1"></textarea>

                <p>In the video artifact, where do you see examples of your practice moving towards your instructional goal?</p>
                <textarea class="textarea" v-model="exercise.self_reflection2_text2"></textarea>

                <p>Analyze one instructional move you captured and what adjustment you would make to move closer to your stated instructional improvement goal.</p>
                <textarea class="textarea" v-model="exercise.self_reflection2_text3"></textarea>

                <p>How has this reflection impacted your thinking about your teaching moving forward?</p>
                <textarea class="textarea" v-model="exercise.self_reflection2_text4"></textarea>

                <p>What will you focus on for the next round of videotaping and what is at least one specific next step you will take to move closer to your goal?</p>
                <textarea class="textarea" v-model="exercise.self_reflection2_text5"></textarea>
              </div>
              <span>
                <button class="button is-info" @click="saveExercise()">Save</button>
                <button class="button is-primary" @click="saveAndAdvance(0)">Home</button>
              </span>
            </template>

            <template v-if="step === 4">
              <div class="box">
                <section style="display: flex; justify-content: space-between;">
                  <h1 :data-balloon=this.toolTipSR data-balloon-pos="down" data-balloon-break>SELF-REFLECTION 3</h1>
                </section>

                <span>Video: (select from drop-down)</span>
                <br />
                <div class="select">
                  <select v-model="exercise.self_reflection3_video">
                    <option v-for="option in videos" v-bind:value="option.id">
                      {{ option.title }}
                    </option>
                  </select>
                </div>
                <br />
                <input type="checkbox" v-model="exercise.self_reflection3_comments" /> Check this box to indicate that you have made at least 5 comments on your video.

                <p>What did you notice?</p>
                <textarea class="textarea" v-model="exercise.self_reflection3_text1"></textarea>

                <p>In the video artifact, where do you see examples of your practice moving towards your instructional goal?</p>
                <textarea class="textarea" v-model="exercise.self_reflection3_text2"></textarea>

                <p>Analyze one instructional move you captured and what adjustment you would make to move closer to your stated instructional improvement goal.</p>
                <textarea class="textarea" v-model="exercise.self_reflection3_text3"></textarea>

                <p>How has this reflection impacted your thinking about your teaching moving forward?</p>
                <textarea class="textarea" v-model="exercise.self_reflection3_text4"></textarea>
              </div>
              <span>
                <button class="button is-info" @click="saveExercise()">Save</button>
                <button class="button is-primary" @click="saveAndAdvance(0)">Home</button>
              </span>
            </template>

            <template v-if="step === 5">
              <div class="box">
                <section style="display: flex; justify-content: space-between;">
                  <h1>COACHING CONVERSATION 1</h1>
                </section>

                <span><h2>Video</h2>(select from drop-down)</span>
                <br />
                <div class="select">
                  <select v-model="exercise.coaching_conversation1_video">
                    <option v-for="option in videos" v-bind:value="option.id">
                      {{ option.title }}
                    </option>
                  </select>
                </div>
                <br />
                <input type="checkbox" v-model="exercise.coaching_conversation1_comments1" /> Check this box to indicate that you have made at least 5 comments on your video.
                <br />
                <input type="checkbox" v-model="exercise.coaching_conversation1_comments2" /> Check this box to indicate that your partner has made at least 5 comments on your video.

                <h2>COACHING CONVERSATION</h2>

                <p>What did you and your partner notice? Complete the coaching conversation template below (with 2-4 statements per category).</p>

                <p style="display:inline;">Clarifying questions and/or statements </p><a target="_blank" href="https://docs.google.com/document/d/13erXndBfz1BfT2OEG0EQIZkKgg9BSN_sOLcnXKmAFPo/edit?ts=5d30733d#bookmark=id.p6r3hqbkicun">(More Info)</a>
                <textarea class="textarea" v-model="exercise.coaching_conversation1_text1"></textarea>

                <p style="display:inline;">Value statements </p><a target="_blank" href="https://docs.google.com/document/d/13erXndBfz1BfT2OEG0EQIZkKgg9BSN_sOLcnXKmAFPo/edit?ts=5d30733d#bookmark=id.660e5nrz8npq">(More Info)</a>
                <textarea class="textarea" v-model="exercise.coaching_conversation1_text2"></textarea>

                <p style="display:inline;">Reflective questions or possibilities </p><a target="_blank" href="https://docs.google.com/document/d/13erXndBfz1BfT2OEG0EQIZkKgg9BSN_sOLcnXKmAFPo/edit?ts=5d30733d#bookmark=id.46bvyxoj18gu">(More Info)</a>
                <textarea class="textarea" v-model="exercise.coaching_conversation1_text3"></textarea>

                <h2>REFLECTION</h2>

                <p>Analyze one instructional move you captured and what adjustment you would make to move closer to your stated goal.</p>
                <textarea class="textarea" v-model="exercise.coaching_conversation1_text4"></textarea>

                <p>How has this coaching conversation impacted your thinking moving forward?</p>
                <textarea class="textarea" v-model="exercise.coaching_conversation1_text5"></textarea>

                <p>Based on this coaching conversation, what will you focus on for the next round of videotaping and what is at least one specific next step you will take to move closer to your goal?</p>
                <textarea class="textarea" v-model="exercise.coaching_conversation1_text6"></textarea>
              </div>
              <span>
                <button class="button is-info" @click="saveExercise()">Save</button>
                <button class="button is-primary" @click="saveAndAdvance(0)">Home</button>
              </span>
            </template>

            <template v-if="step === 6">
              <div class="box">
                <section style="display: flex; justify-content: space-between;">
                  <h1>COACHING CONVERSATION 2</h1>
                </section>

                <span><h2>Video</h2>(select from drop-down)</span>
                <br />
                <div class="select">
                  <select v-model="exercise.coaching_conversation2_video">
                    <option v-for="option in videos" v-bind:value="option.id">
                      {{ option.title }}
                    </option>
                  </select>
                </div>
                <br />
                <input type="checkbox" v-model="exercise.coaching_conversation2_comments1" /> Check this box to indicate that you have made at least 5 comments on your video.
                <br />
                <input type="checkbox" v-model="exercise.coaching_conversation2_comments2" /> Check this box to indicate that your partner has made at least 5 comments on your video.

                <h2>COACHING CONVERSATION</h2>

                <p>What did you and your partner notice? Complete the coaching conversation template below (with 2-4 statements per category).</p>

                <p style="display:inline;">Clarifying questions and/or statements </p><a target="_blank" href="https://docs.google.com/document/d/13erXndBfz1BfT2OEG0EQIZkKgg9BSN_sOLcnXKmAFPo/edit?ts=5d30733d#bookmark=id.p6r3hqbkicun">(More Info)</a>
                <textarea class="textarea" v-model="exercise.coaching_conversation2_text1"></textarea>

                <p style="display:inline;">Value statements </p><a target="_blank" href="https://docs.google.com/document/d/13erXndBfz1BfT2OEG0EQIZkKgg9BSN_sOLcnXKmAFPo/edit?ts=5d30733d#bookmark=id.660e5nrz8npq">(More Info)</a>
                <textarea class="textarea" v-model="exercise.coaching_conversation2_text2"></textarea>

                <p style="display:inline;">Reflective questions or possibilities </p><a target="_blank" href="https://docs.google.com/document/d/13erXndBfz1BfT2OEG0EQIZkKgg9BSN_sOLcnXKmAFPo/edit?ts=5d30733d#bookmark=id.46bvyxoj18gu">(More Info)</a>
                <textarea class="textarea" v-model="exercise.coaching_conversation2_text3"></textarea>

                <h2>REFLECTION</h2>

                <p>Analyze one instructional move you captured and what adjustment you would make to move closer to your stated goal.</p>
                <textarea class="textarea" v-model="exercise.coaching_conversation2_text4"></textarea>

                <p>How has this coaching conversation impacted your thinking moving forward?</p>
                <textarea class="textarea" v-model="exercise.coaching_conversation2_text5"></textarea>

                <p>Based on this coaching conversation, what will you focus on for the next round of videotaping and what is at least one specific next step you will take to move closer to your goal?</p>
                <textarea class="textarea" v-model="exercise.coaching_conversation2_text6"></textarea>
              </div>
              <span>
                <button class="button is-info" @click="saveExercise()">Save</button>
                <button class="button is-primary" @click="saveAndAdvance(0)">Home</button>
              </span>
            </template>

            <template v-if="step === 7">
              <div class="box">
                <section style="display: flex; justify-content: space-between;">
                  <h1>COACHING CONVERSATION 3</h1>
                </section>

                <span><h2>Video</h2>(select from drop-down)</span>
                <br />
                <div class="select">
                  <select v-model="exercise.coaching_conversation3_video">
                    <option v-for="option in videos" v-bind:value="option.id">
                      {{ option.title }}
                    </option>
                  </select>
                </div>
                <br />
                <input type="checkbox" v-model="exercise.coaching_conversation3_comments1" /> Check this box to indicate that you have made at least 5 comments on your video.
                <br />
                <input type="checkbox" v-model="exercise.coaching_conversation3_comments2" /> Check this box to indicate that your partner has made at least 5 comments on your video.

                <h2>COACHING CONVERSATION</h2>

                <p>What did you and your partner notice? Complete the coaching conversation template below (with 2-4 statements per category).</p>

                <p style="display:inline;">Clarifying questions and/or statements </p><a target="_blank" href="https://docs.google.com/document/d/13erXndBfz1BfT2OEG0EQIZkKgg9BSN_sOLcnXKmAFPo/edit?ts=5d30733d#bookmark=id.p6r3hqbkicun">(More Info)</a>
                <textarea class="textarea" v-model="exercise.coaching_conversation3_text1"></textarea>

                <p style="display:inline;">Value statements </p><a target="_blank" href="https://docs.google.com/document/d/13erXndBfz1BfT2OEG0EQIZkKgg9BSN_sOLcnXKmAFPo/edit?ts=5d30733d#bookmark=id.660e5nrz8npq">(More Info)</a>
                <textarea class="textarea" v-model="exercise.coaching_conversation3_text2"></textarea>

                <p style="display:inline;">Reflective questions or possibilities </p><a target="_blank" href="https://docs.google.com/document/d/13erXndBfz1BfT2OEG0EQIZkKgg9BSN_sOLcnXKmAFPo/edit?ts=5d30733d#bookmark=id.46bvyxoj18gu">(More Info)</a>
                <textarea class="textarea" v-model="exercise.coaching_conversation3_text3"></textarea>

                <h2>REFLECTION</h2>

                <p>Analyze one instructional move you captured and what adjustment you would make to move closer to your stated goal.</p>
                <textarea class="textarea" v-model="exercise.coaching_conversation3_text4"></textarea>

                <p>How has this coaching conversation impacted your thinking moving forward?</p>
                <textarea class="textarea" v-model="exercise.coaching_conversation2_text5"></textarea>
              </div>
              <span>
                <button class="button is-info" @click="saveExercise()">Save</button>
                <button class="button is-primary" @click="saveAndAdvance(0)">Home</button>
              </span>
            </template>


            <template v-if="step === 8">

              <div class="box">
                <section style="display: flex; justify-content: space-between;">
                  <h1>FINAL REFLECTION</h1>
                </section>

                <p>Review your original goal and rationale and explain how that has evolved over time, as captured in ClassForward embedded reflections</p>

                <h2>Reflection on goal</h2>
                <p>Analyze at least three instructional moves related to your goal from your most recent video</p>
                <ul>
                  <li>Provide at least 2 examples of instructional adjustments based on student response (Ex. My intention was to do X; when I saw/heard students doing Y, this then made me do Z)</li>
                  <li>Provide at least 1 example of an instructional adjustment you could have made based on a student response. (ex: my intention was to do X; when I saw/heard students doing Y, I did Z. An alternative could be B and the expected impact is C.)</li>
                </ul>
                <textarea class="textarea" v-model="exercise.final_reflection_text1"></textarea>
                <h2>Reflection on practice</h2>
                <p>Throughout DIY Flex, how has your practice grown toward your goal? What implications does this have for your future teaching?</p>
                <textarea class="textarea" v-model="exercise.final_reflection_text2"></textarea>
              </div>
                <button class="button is-info" @click="saveExercise()">Save</button>
                <button class="button is-primary" @click="saveAndAdvance(0)">Home</button>
                <button class="button is-primary" v-if="this.completionStatus.all" @click="exportToPDF()">
                Export as PDF
              </button>
            </template>
          </div>
        </div>
      </section>
    </BasePage>
  </template>

<script>
import Loader from './Loader'
import BasePage from './BasePage.vue'
import ChevronIcon from './ChevronIcon.vue'
import ExerciseDropdown from './ExerciseDropdown.vue'

import { router } from '../main.js'

import axios from 'axios'
import store from '../store.js'

import CircledCheckMarkIcon from './CircledCheckMarkIcon.vue'

const is404Error = error =>
  error.response != null && error.response.status === 404

const isNotEmpty = string => (string && string.length)

export default {
  name: 'Exercise',
  components: {
    BasePage,
    Loader,
    ExerciseDropdown,
    CircledCheckMarkIcon,
    ChevronIcon,
  },
  async mounted () {
    await Promise.all([
      store.dispatch('fetchVideos'),
      this.loadExercise(),
    ])
    this.loading = false
  },
  computed: {
    videos () {
      return store.state.videos
    },
    selfReflection1 () {
      return (isNotEmpty(this.exercise.self_reflection1_text1) &&
        isNotEmpty(this.exercise.self_reflection1_text2) &&
        isNotEmpty(this.exercise.self_reflection1_text3) &&
        isNotEmpty(this.exercise.self_reflection1_text4) &&
        isNotEmpty(this.exercise.self_reflection1_text5))
    },
    selfReflection2 () {
      return (isNotEmpty(this.exercise.self_reflection2_text1) &&
        isNotEmpty(this.exercise.self_reflection2_text2) &&
        isNotEmpty(this.exercise.self_reflection2_text3) &&
        isNotEmpty(this.exercise.self_reflection2_text4) &&
        isNotEmpty(this.exercise.self_reflection2_text5))
    },
    selfReflection3 () {
      return (isNotEmpty(this.exercise.self_reflection3_text1) &&
        isNotEmpty(this.exercise.self_reflection3_text2) &&
        isNotEmpty(this.exercise.self_reflection3_text3) &&
        isNotEmpty(this.exercise.self_reflection3_text4) &&
        isNotEmpty(this.exercise.self_reflection3_text5))
    },
    coachingConversation1Coaching () {
      return (isNotEmpty(this.exercise.coaching_conversation1_text1) &&
        isNotEmpty(this.exercise.coaching_conversation1_text2) &&
        isNotEmpty(this.exercise.coaching_conversation1_text3))
    },
    coachingConversation1Reflection () {
      return (isNotEmpty(this.exercise.coaching_conversation1_text4) &&
        isNotEmpty(this.exercise.coaching_conversation1_text5) &&
        isNotEmpty(this.exercise.coaching_conversation1_text6))
    },
    coachingConversation2Coaching () {
      return (isNotEmpty(this.exercise.coaching_conversation2_text1) &&
        isNotEmpty(this.exercise.coaching_conversation2_text2) &&
        isNotEmpty(this.exercise.coaching_conversation2_text3))
    },
    coachingConversation2Reflection () {
      return (isNotEmpty(this.exercise.coaching_conversation2_text4) &&
        isNotEmpty(this.exercise.coaching_conversation2_text5) &&
        isNotEmpty(this.exercise.coaching_conversation2_text6))
    },
    coachingConversation3Coaching () {
      return (isNotEmpty(this.exercise.coaching_conversation3_text1) &&
        isNotEmpty(this.exercise.coaching_conversation3_text2) &&
        isNotEmpty(this.exercise.coaching_conversation3_text3))
    },
    coachingConversation3Reflection () {
      return (isNotEmpty(this.exercise.coaching_conversation3_text4) &&
        isNotEmpty(this.exercise.coaching_conversation3_text5))
    },
    completionStatus () {
      const introReflectionComplete = isNotEmpty(this.exercise.intro_reflection_text)

      const selfReflection1Complete = (this.exercise.self_reflection1_video && this.selfReflection1)

      const selfReflection2Complete = (this.exercise.self_reflection2_video && this.selfReflection2)

      const selfReflection3Complete = (this.exercise.self_reflection3_video && this.selfReflection3)

      const coachingConversation1Complete = (this.exercise.coaching_conversation1_video && this.coachingConversation1Coaching && this.coachingConversation1Reflection)

      const coachingConversation2Complete = (this.exercise.coaching_conversation2_video && this.coachingConversation2Coaching && this.coachingConversation2Reflection)

      const coachingConversation3Complete = (this.exercise.coaching_conversation3_video && this.coachingConversation3Coaching && this.coachingConversation3Reflection)

      const finalReflectionComplete = (isNotEmpty(this.exercise.final_reflection_text1) && isNotEmpty(this.exercise.final_reflection_text2))

      return {
        'introReflectionComplete': introReflectionComplete,
        'selfReflection1Complete': selfReflection1Complete,
        'selfReflection2Complete': selfReflection2Complete,
        'selfReflection3Complete': selfReflection3Complete,
        'coachingConversation1Complete': coachingConversation1Complete,
        'coachingConversation2Complete': coachingConversation2Complete,
        'coachingConversation3Complete': coachingConversation3Complete,
        'finalReflectionComplete': finalReflectionComplete,
        'all': (introReflectionComplete && selfReflection1Complete && selfReflection2Complete && selfReflection3Complete &&
                coachingConversation1Complete && coachingConversation2Complete && coachingConversation3Complete && finalReflectionComplete)
      }
    }

  },
  methods: {
    saveAndBack () {
      this.step = this.step - 1
      this.saveExercise(false)
    },
    saveAndNext () {
      this.step = this.step + 1
      this.saveExercise(false)
    },
    saveAndAdvance (index) {
      this.step = index
      this.saveExercise(false)
    },
    async loadExercise () {
      store.commit('setLoadingExercises', true)
      return axios.get('/api/v1/exercises/active/',
        { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(response => response.data)
      .then(exercise => {
        this.exercise = exercise
        store.commit('setLoadingExercises', false)
      })
      .catch(error => {
        console.warn('error fetching active exercise', error)
        store.commit('setLoadingExercises', false)
        if (is404Error(error)) {
          router.replace('/404')
        }
        throw error
      })
    },
    exportToPDF () {
      alert('This feature is not finished yet')
    },
    async saveExercise (showToast = true) {
      return axios.patch(`/api/v1/exercises/${this.exercise.id}/`, this.exercise,
        { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(() => {
        if (showToast) {
          store.commit('notify', { message: 'Saved successfully', level: 'info' })
        }
        console.log('saved...')
      })
      .catch(error => store.dispatch(store.action.generalErrorHandler, { error, message: "Uh Oh. Group information couldn't be updated." }))
    },
  },
  data () {
    return {
      loading: true,
      steps: 10,
      step: 0,
      exercise: null,
      selfReflection1Video: 0,
      selfReflection2Video: 0,
      selfReflection3Video: 0,
      coachingConversation1Video: 0,
      coachingConversation2Video: 0,
      coachingConversation3Video: 0,
      displayCC: false,
      displaySR: false,
      toolTipSR: `For each self reflection, you will:\n
                - record 10-15 minutes of instruction capturing instruction related to your goal\n
                - watch and analyze your video, making at least 5 comments\n
                - complete the reflection questions in ClassForward`,
    }
  },
}
</script>

<style lang="scss" scoped>

#intro {
  margin-bottom: 2rem;
}

textarea {
  margin-bottom: 1rem;
}

p {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

h1 {
  border-bottom: 2px solid #000;
  font-weight: 500;
  margin-bottom: 1rem;
  font-size: 1.5rem;
}

h2 {
  font-weight: 500;
  margin-top: 2rem;
  margin-bottom: 1rem;
  font-size: 1.25rem;
}

.exercise--title {
  margin-bottom: 0.5rem;
}

.no-questions {
  text-align: center;
  font-style: italic;
  margin-bottom: 5px;
  font-size: 2rem;
  font-weight: 300;
}

ul {
  list-style: circle;
  padding-left: 2rem;
  margin-top: .25rem;
  margin-bottom: .25rem;
}

.left {
  padding-left: 3rem;
}

div.menu-list > div {
  text-align: center;
  background-color: #fff;
  padding: 2rem;
  list-style: none;
  margin-top: 1rem;
  border: 2px solid #000;
  box-shadow: 4px -4px #23c6c8;
  font-size: 2rem;
  cursor: pointer;
  color: black;
}

.upside-down {
  transform: rotate( 180deg );
  transition: transform 500ms ease;
}

textarea {
  margin-top: 1rem;
  border: 2px solid #000;
  box-shadow: 4px -4px #23c6c8;

}

</style>



// WEBPACK FOOTER //
// Exercise.vue?13191c11